import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminSidebar } from '@/components/AdminSidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart, Line, LineChart, ResponsiveContainer, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Separator } from '@/components/ui/separator';
import { ArrowUpRight, Users, MessageSquare, FileText, TrendingUp, Calendar } from 'lucide-react';
import PageTransition from '@/components/PageTransition';
import { toast } from 'sonner';

// Sample data for the dashboard
const monthlyVisits = [
  { name: 'Jan', visits: 1250 },
  { name: 'Feb', visits: 1800 },
  { name: 'Mar', visits: 1600 },
  { name: 'Apr', visits: 2200 },
  { name: 'May', visits: 2800 },
  { name: 'Jun', visits: 3500 },
];

const weeklyConversions = [
  { name: 'Mon', premium: 5, free: 18 },
  { name: 'Tue', premium: 8, free: 12 },
  { name: 'Wed', premium: 6, free: 15 },
  { name: 'Thu', premium: 9, free: 20 },
  { name: 'Fri', premium: 7, free: 25 },
  { name: 'Sat', premium: 4, free: 17 },
  { name: 'Sun', premium: 3, free: 14 },
];

const AdminDashboard = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Check if the user is authenticated as admin
    const authData = localStorage.getItem('adminAuth');
    if (!authData || !JSON.parse(authData).authenticated) {
      toast.error('You must be logged in as admin to view this page');
      navigate('/login', { state: { isAdmin: true } });
    }
  }, [navigate]);

  return (
    <div className="flex min-h-screen">
      <AdminSidebar />
      
      <div className="flex-1 p-6 overflow-y-auto">
        <PageTransition>
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">Admin - Dashboard</h1>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm">
                <Calendar className="mr-2 h-4 w-4" />
                Last 30 Days
              </Button>
              <Button variant="outline" size="sm">
                Export
              </Button>
            </div>
          </div>
          
          <Separator className="mb-6" />
          
          {/* Stats Row */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Total Users</CardDescription>
                <CardTitle className="text-2xl">2,856</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center text-sm text-green-500">
                  <ArrowUpRight className="mr-1 h-4 w-4" />
                  <span>12% from last month</span>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>AI Conversations</CardDescription>
                <CardTitle className="text-2xl">1,428</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center text-sm text-green-500">
                  <ArrowUpRight className="mr-1 h-4 w-4" />
                  <span>24% from last month</span>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Plan Conversions</CardDescription>
                <CardTitle className="text-2xl">368</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center text-sm text-green-500">
                  <ArrowUpRight className="mr-1 h-4 w-4" />
                  <span>8% from last month</span>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Blog Engagement</CardDescription>
                <CardTitle className="text-2xl">752</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center text-sm text-green-500">
                  <ArrowUpRight className="mr-1 h-4 w-4" />
                  <span>18% from last month</span>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Charts Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Card>
              <CardHeader>
                <CardTitle>Monthly Site Visitors</CardTitle>
                <CardDescription>
                  Total traffic over the past 6 months
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={monthlyVisits}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="visits" stroke="#33C3F0" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Plan Conversion Rate</CardTitle>
                <CardDescription>
                  Free vs. Premium plan conversions this week
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weeklyConversions}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="premium" fill="#33C3F0" />
                      <Bar dataKey="free" fill="#E5E7EB" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => navigate('/admin/ai-settings')}>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">AI Chat Settings</CardTitle>
                  <CardDescription>Manage chatbot behavior and responses</CardDescription>
                </div>
                <MessageSquare className="h-6 w-6 text-muted-foreground" />
              </CardHeader>
            </Card>
            
            <Card className="hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => navigate('/blog-admin')}>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Blog Management</CardTitle>
                  <CardDescription>Edit and publish blog content</CardDescription>
                </div>
                <FileText className="h-6 w-6 text-muted-foreground" />
              </CardHeader>
            </Card>
            
            <Card className="hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => navigate('/metrics')}>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Site Metrics</CardTitle>
                  <CardDescription>View detailed analytics</CardDescription>
                </div>
                <TrendingUp className="h-6 w-6 text-muted-foreground" />
              </CardHeader>
            </Card>
          </div>
        </PageTransition>
      </div>
    </div>
  );
};

export default AdminDashboard;
